import React, { useState, useRef } from "react";
import "./contentpage.css";
import Question from "../components/Question";
import LandingSection from "./LandingSection";
import FloatingPageNav from "../components/FloatingPageNav";

function IslamicGuide63() {
  const contentRef = useRef(null);
  const titleText =
    "Decentralized Money: A Return to Islamic Financial Principles";
  const links = {
    prev: "/islamicguide-62",
    index: "/indexislamic-guide",
    next: "/islamicguide-64",
  };

  const questions = [
    {
      text: "1. In Islamic history, the issuance and circulation of gold and silver coins was determined by:",
      options: [
        { value: "A", label: "A. Central planners" },
        { value: "B", label: "B. Religious authorities" },
        { value: "C", label: "C. Demand and supply" },
        { value: "D", label: "D. Government monopolies" },
      ],
      correct: "C",
    },
    {
      text: "2. Why is Bitcoin’s decentralisation considered a virtue rather than a weakness?",
      options: [
        {
          value: "A",
          label: "A. It allows governments to control it more easily",
        },
        {
          value: "B",
          label: "B. It removes the need for mathematics and code",
        },
        {
          value: "C",
          label: "C. It prevents manipulation by a single authority",
        },
        { value: "D", label: "D. It depends entirely on one inventor" },
      ],
      correct: "C",
    },
    {
      text: "3. The criticism that the identity of Bitcoin’s inventor is unknown is irrelevant because:",
      options: [
        { value: "A", label: "A. The inventor still controls the network" },
        {
          value: "B",
          label:
            "B. Bitcoin is governed by verifiable laws of mathematics and computer science",
        },
        { value: "C", label: "C. Governments secretly back the system" },
        { value: "D", label: "D. It was co-created by central planners" },
      ],
      correct: "B",
    },
    {
      text: "4. Who maintains Bitcoin’s integrity and ensures that no single entity can manipulate it?",
      options: [
        { value: "A", label: "A. A central bank" },
        { value: "B", label: "B. The pseudonymous inventor" },
        {
          value: "C",
          label: "C. Thousands of developers and node operators",
        },
        { value: "D", label: "D. A global regulatory body" },
      ],
      correct: "C",
    },
  ];

  // State to track selected answers for all questions
  const [answers, setAnswers] = useState({});

  // Function to update an answer
  const handleAnswerChange = (questionIndex, selectedValue) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: selectedValue }));
  };

  // Compute score
  const score = questions.reduce((total, q, index) => {
    return total + (answers[index] === q.correct ? 1 : 0);
  }, 0);

  return (
    <div className="container">
      <LandingSection
        titleText={titleText}
        scrollToRef={contentRef}
        progressValue={63}
        timerMinutes={60}
        currentLevel={5}
        unlockedBadges={9}
      />

      <p className="content" ref={contentRef}>
        <b>The Myth of Central Authority as a Requirement for Money</b>
        <br />
        <br />
        One of the most common objections raised against Bitcoin is the claim
        that it cannot be considered “real money” because it is not issued by a
        government or tied to the authority of a central bank. This critique is
        rooted in the modern conception of fiat currency, where governments
        declare that a certain piece of paper has value simply because it
        carries their decree. Yet if we examine the history of money across
        civilizations, including the long and rich tradition of Islamic
        commerce, it becomes clear that this assumption is misguided. Money did
        not originate through the authority of kings or governments, nor was its
        value derived from a central planner’s decree. Instead, money evolved in
        free and open markets where people collectively decided what commodities
        would best serve the functions of exchange, store of value, and unit of
        account.
        <br />
        In Islamic history, gold and silver coins were used extensively as
        money. Their value was not determined by a ministry of finance or a
        central authority but rather by the forces of supply and demand. A
        merchant in Damascus, a farmer in Cairo, or a trader in Baghdad did not
        need to consult a ruler to determine what their gold or silver coin was
        worth. Instead, the value emerged organically in the marketplace,
        negotiated between buyer and seller, grounded in weight and purity. The
        legitimacy of the currency rested not in an institution but in the
        shared understanding among market participants. This arrangement ensured
        that no central authority could arbitrarily debase the currency or
        manipulate its supply for political gain.
        <br />
        <br />
        <b>The Pseudonymity of Bitcoin’s Inventor: A Feature, Not a Flaw</b>
        <br />
        <br />
        Another critique often aimed at Bitcoin is that its inventor is unknown,
        and therefore, the system lacks credibility. Critics argue that since
        the identity of the creator is hidden, the project cannot be trusted,
        implying that a transparent founder or institutional body is necessary
        for legitimacy. However, this argument collapses under closer scrutiny.
        The value of an idea or a system does not depend on who its creator is
        but on whether its principles can be verified, tested, and trusted in
        practice. Mathematics, science, and technology are full of examples
        where discoveries and inventions hold true regardless of the personal
        identity of the inventor.
        <br />
        Consider a simple mathematical law such as the Pythagorean theorem. No
        one questions the validity of the equation a² + b² = c² in a
        right-angled triangle because its truth is verifiable. Its correctness
        does not rest on knowing the biographical details of the mathematician
        who formalised it but on the fact that it is universally observable.
        Bitcoin operates in a similar fashion. It is not based on blind trust in
        a person but on open-source code, computer science, cryptographic
        proofs, and economic principles that can be audited and verified by
        anyone, anywhere in the world. The rules of Bitcoin are transparent and
        enforced by thousands of independent computers running the network.
        These rules cannot be bent to favor one individual, not even the
        pseudonymous inventor.
        <br />
        <br />
        <b>The Collective Security of a Decentralised Network</b>
        <br />
        <br />
        One of Bitcoin’s greatest strengths, and a key response to critics, is
        the fact that it is not governed or controlled by a single entity.
        Instead, it is maintained by a decentralised network of participants
        that includes developers, miners, and node operators. This collective
        structure makes Bitcoin resilient, transparent, and immune to
        manipulation by one party. Thousands of developers across the world have
        studied, audited, and improved Bitcoin’s codebase over the years. These
        contributions are peer-reviewed, publicly available, and subject to
        scrutiny by anyone with the knowledge to evaluate them. No single
        developer or organisation has the ability to secretly change the rules
        without consensus from the broader network.
        <br />
        In addition, Bitcoin’s rules are enforced by node operators, individuals
        who download the software and independently verify transactions and
        blocks. These nodes act as guardians of the protocol, ensuring that no
        invalid transactions are accepted and that the supply of Bitcoin remains
        capped at 21 million. Unlike fiat currency, where people must blindly
        trust that central planners are not printing excess money, Bitcoin’s
        supply and rules are enforced mathematically and distributed across tens
        of thousands of computers worldwide. This makes the network highly
        resistant to corruption, censorship, and centralised manipulation.
        <br />
        <br />
        <br />
        <b>Bitcoin and the Alignment with Islamic Principles</b>
        <br />
        <br />
        When critics argue that Bitcoin is flawed because it lacks a central
        issuer or because its creator is pseudonymous, they are unknowingly
        imposing modern fiat assumptions onto a system that operates by entirely
        different, and arguably more ethical, rules. In Islamic history, money
        was not defined by state decree but by the consensus of the market and
        the community. Gold and silver coins circulated freely across regions
        and dynasties, their legitimacy anchored in weight, purity, and
        acceptance by the people. Bitcoin reflects this same dynamic. It derives
        its worth not from a government’s stamp of approval but from the
        voluntary adoption of individuals who see its value in providing secure,
        scarce, and verifiable money.
        <br />
        Furthermore, the critique that Bitcoin’s inventor is unknown holds no
        weight when examined against the principles of Islamic jurisprudence and
        reasoning. In Islam, what matters is not who a person is but whether
        their actions and ideas align with truth, justice, and fairness. A fair
        contract remains valid regardless of the ethnicity or status of the
        person drafting it. A just weight on a scale remains just regardless of
        whether the craftsman who built the scale is known or unknown.
        Similarly, Bitcoin’s rules remain valid and fair regardless of the
        identity of its creator, because they can be tested, verified, and
        trusted in practice.
        <br />
        The larger point is that Bitcoin represents a decentralised,
        incorruptible form of money that avoids the pitfalls of modern fiat
        systems. Its supply cannot be inflated, its rules cannot be altered by
        decree, and its operation cannot be manipulated by one central figure.
        These are not violations of Islamic principles but rather alignments
        with them. Islam emphasizes fairness, accountability, and the prevention
        of unjust enrichment at the expense of others. Bitcoin embodies these
        principles by ensuring that no central planners can debase it, no ruler
        can alter it for political gain, and no single entity can exploit it at
        the expense of the community. Its decentralisation, far from being a
        flaw, is the very characteristic that makes it consistent with the
        spirit of justice in Islamic economic thought.
        <br />
        <br />
        <br />
        <b>Summary of Today’s Lesson</b>
        <br />
        <br />
        The lesson highlights that criticisms of Bitcoin for not being backed by
        a country or for having an pseudonymous inventor are misplaced. Islamic
        history shows that money such as gold and silver circulated without
        central decree, guided instead by natural forces of demand and supply.
        Similarly, Bitcoin’s decentralised nature is a strength rather than a
        flaw, as it prevents manipulation by a single authority. The
        pseudonymity of its inventor is irrelevant, since Bitcoin operates on
        verifiable mathematical and economic principles, much like universally
        accepted laws in mathematics that do not depend on who first discovered
        them. With contributions from thousands of developers and continuous
        auditing by independent node operators, Bitcoin’s resilience and
        integrity are maintained collectively, ensuring alignment with both
        practical reality and Islamic principles of fairness and
        decentralisation.
        <br />
        <br />
        <b> Coming Up Next</b>
        <br />
        <br />
        In our next lesson, we will shift our focus to the intersection of
        Bitcoin and digital privacy. While we have seen how Islam emphasizes
        safeguarding personal and financial information as a matter of dignity
        and trust, the next step is to understand how Bitcoin, with its
        pseudonymous ledger, provides Muslims with a practical means to uphold
        these values in today’s surveillance-driven world. We will explore how
        Bitcoin transactions differ from traditional banking, why pseudonymity
        matters, and how Muslims can use this technology responsibly to protect
        their financial independence while staying true to their faith.
      </p>
      <h2>Test your understanding</h2>

      {questions.map((q, index) => (
        <Question
          key={index}
          questionText={q.text}
          options={q.options}
          correctAnswer={q.correct}
          onAnswer={(value) => handleAnswerChange(index, value)}
        />
      ))}

      <div style={{ marginTop: "20px", fontWeight: "bold", fontSize: "1.2em" }}>
        Score: {score} / {questions.length}
      </div>

      <FloatingPageNav
        prevLink={links.prev}
        indexLink={links.index}
        nextLink={links.next}
      />
    </div>
  );
}

export default IslamicGuide63;
